import { z } from 'zod/mini';
import { accessTokenSchema } from './access';
import { AVATARS, type Avatar } from './avatars';
import { DEFAULT_SETTINGS, GAME_MODES, type GameSettings, type PanoRef, type RevealedLocation } from './game';
import { gameSettingsSchema, latLngSchema } from './game';
import { DEFAULT_PICKS_EACH, PICKS_EACH_OPTIONS, type PickPreview, type pickPreviewSchema, type pickSchema } from './picks';
import { DEFAULT_STARTING_HEALTH, SURVIVAL_HEALTH_OPTIONS } from './survival';
import type { LatLng } from './geo';

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 4;

export const roomCodeSchema = z
  .string()
  .check(z.trim(), z.toUpperCase(), z.regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`), 'Invalid room code'));

/**
 * Removes control and invisible formatting characters (bidi overrides, zero-width spaces…)
 * that could disguise a name as someone else's, and squeezes whitespace.
 */
export function cleanNickname(name: string): string {
  return name.replace(/[\p{Cc}\p{Cf}]/gu, '').replace(/\s+/g, ' ').trim();
}

export const playerProfileSchema = z.object({
  name: z.pipe(
    z.pipe(z.string().check(z.maxLength(64)), z.transform(cleanNickname)),
    z.string().check(z.minLength(1, 'Pick a nickname'), z.maxLength(20, 'Nickname is too long')),
  ),
  avatar: z.enum(AVATARS),
});

export type PlayerProfile = z.infer<typeof playerProfileSchema>;

/** Secret generated by each browser tab; lets a player reconnect to their seat. */
export const playerTokenSchema = z.string().check(z.minLength(16), z.maxLength(64));

/** Options that only make sense with other players. */
const partyOptionsShape = {
  /** Quick guesses earn up to 25% more (see `speedBonus`). */
  speedBonus: z.boolean(),
  /** Players split into two teams; a team scores the average of its players' points. */
  teams: z.boolean(),
  /** Players may take a locked guess back while the round is still running. */
  unguess: z.boolean(),
  /** Survival: players (or teams) lose health each round; the last one standing wins. */
  survival: z.boolean(),
  /** Health everyone starts a survival game with. */
  startingHealth: z.int().check(z.minimum(SURVIVAL_HEALTH_OPTIONS[0]), z.maximum(SURVIVAL_HEALTH_OPTIONS.at(-1)!)),
  /** Take turns: players take turns choosing the place the others guess (see `picks.ts`). */
  picks: z.boolean(),
  /** How many times each player picks. */
  picksEach: z.int().check(z.minimum(PICKS_EACH_OPTIONS[0]), z.maximum(PICKS_EACH_OPTIONS.at(-1)!)),
};
/** Options added later have defaults, so options saved before them still load. */
export const partyOptionsSchema = z.object({
  ...partyOptionsShape,
  unguess: z._default(z.boolean(), false),
  survival: z._default(z.boolean(), false),
  startingHealth: z._default(partyOptionsShape.startingHealth, DEFAULT_STARTING_HEALTH),
  picks: z._default(z.boolean(), false),
  picksEach: z._default(partyOptionsShape.picksEach, DEFAULT_PICKS_EACH),
});
export type PartyOptions = z.infer<typeof partyOptionsSchema>;
export const DEFAULT_PARTY_OPTIONS: PartyOptions = {
  speedBonus: false,
  teams: false,
  unguess: false,
  survival: false,
  startingHealth: DEFAULT_STARTING_HEALTH,
  picks: false,
  picksEach: DEFAULT_PICKS_EACH,
};

/** Survival and take turns don't mix: switching one on switches the other off. */
export function exclusivePartyOptions<T extends PartyOptions>(settings: T, patch: Partial<PartyOptions> = {}): T {
  if (patch.picks) return { ...settings, survival: false };
  if (patch.survival || (settings.survival && settings.picks)) return { ...settings, picks: false };
  return settings;
}

export type PartySettings = GameSettings & PartyOptions;
export const DEFAULT_PARTY_SETTINGS: PartySettings = { ...DEFAULT_SETTINGS, ...DEFAULT_PARTY_OPTIONS };

export const TEAMS = [
  { name: 'Team Sun', short: 'Sun', emoji: '☀️', color: '#ffb547' },
  { name: 'Team Moon', short: 'Moon', emoji: '🌙', color: '#7aa7ff' },
] as const;
export type TeamIndex = 0 | 1;
const teamIndexSchema = z.union([z.literal(0), z.literal(1)]);
export const teamSchema = z.object({ team: teamIndexSchema });

export const DEFAULT_TEAM_NAMES: [string, string] = [TEAMS[0].name, TEAMS[1].name];
export const teamNameSchema = z.object({
  team: teamIndexSchema,
  name: z.pipe(
    z.pipe(z.string().check(z.maxLength(64)), z.transform(cleanNickname)),
    z.string().check(z.minLength(1, 'Pick a team name'), z.maxLength(24, 'Team name is too long')),
  ),
});

export const createPartySchema = z.object({
  token: playerTokenSchema,
  profile: playerProfileSchema,
  settings: z.optional(gameSettingsSchema),
  options: z.optional(partyOptionsSchema),
  /** Needed when the server is private (see `ACCESS_DENIED_MESSAGE`). */
  accessToken: z.optional(accessTokenSchema),
});

export const joinPartySchema = z.object({
  token: playerTokenSchema,
  code: roomCodeSchema,
  profile: playerProfileSchema,
  /** False when an open page reconnects on its own (it doesn't reload the panorama). */
  reloaded: z.optional(z.boolean()),
});

/**
 * A change to some settings. Nothing has a default here: a patch without the mode (or an
 * option) must leave it alone, not reset it.
 */
export const updateSettingsSchema = z.partial(
  z.object({ ...gameSettingsSchema.shape, mode: z.enum(GAME_MODES), ...partyOptionsShape }),
);

/** Starting a game spends Street View panoramas: on a private server it needs an access code too. */
export const startGameSchema = z.object({ accessToken: z.optional(accessTokenSchema) });

export const partyGuessSchema = z.object({
  roundIndex: z.int().check(z.minimum(0)),
  guess: latLngSchema,
});

/** Where the player's pin currently is, before they lock it in. It counts if time runs out. */
export const partyPinSchema = partyGuessSchema;

/** Takes a locked guess back (when the party allows it). */
export const unguessSchema = z.object({ roundIndex: z.int().check(z.minimum(0)) });

export const kickSchema = z.object({ playerId: z.string().check(z.minLength(1)) });

/** A spectator (the TV screen): sees the party, takes no seat, loads no panorama. */
export const watchSchema = z.object({ code: roomCodeSchema });

/**
 * Emojis players can send to the whole party (outside rounds: no distractions while guessing).
 * The first `QUICK_REACTIONS` are always on screen, the rest one tap away.
 */
export const REACTIONS = [
  '😂', '🔥', '🤯', '👏', '😱', '🫡',
  '😭', '💀', '🤡', '🎉', '👀', '🤔', '😎', '🥶', '🙈', '🤌', '💯', '🐐',
  '🍀', '🗺️', '🧭', '🚀', '😴', '🤦',
] as const;
export const QUICK_REACTIONS = 6;
export type Reaction = (typeof REACTIONS)[number];
export const reactSchema = z.object({ emoji: z.enum(REACTIONS) });

export interface PartyReaction {
  playerId: string;
  emoji: Reaction;
}

/** Longest chat message, in characters. */
export const CHAT_MAX_LENGTH = 200;
/** Messages a party keeps (and sends to newcomers). */
export const CHAT_HISTORY = 50;

/** One line of text, without control or invisible characters. */
export const chatSchema = z.object({
  text: z.pipe(
    z.pipe(z.string().check(z.maxLength(CHAT_MAX_LENGTH * 2)), z.transform(cleanNickname)),
    z.string().check(z.minLength(1, 'Type a message'), z.maxLength(CHAT_MAX_LENGTH, 'That message is too long')),
  ),
});

export interface ChatMessage {
  id: number;
  playerId: string;
  /** The sender as they were when they wrote, so messages survive them leaving. */
  name: string;
  avatar: Avatar;
  color: string;
  text: string;
  at: number;
}

/** Chat is closed while a round is played: no spoilers, no hints. */
export function chatOpen(phase: PartyPhase['kind']): boolean {
  return phase !== 'intro' && phase !== 'guessing';
}

export interface PartyPlayer {
  id: string;
  name: string;
  avatar: Avatar;
  color: string;
  isHost: boolean;
  connected: boolean;
  totalScore: number;
  /** Null unless the party plays in teams. */
  team: TeamIndex | null;
  /** Survival only (else null): health left; below zero when knocked out, for tie-breaks. */
  health: number | null;
  /** Survival only: the round the player (or their team) was knocked out in. */
  outInRound: number | null;
}

export interface PartyGuess {
  playerId: string;
  guess: LatLng | null;
  distanceKm: number | null;
  /** Speed bonus included. */
  points: number;
  /** The part of `points` earned by guessing quickly (0 without the speed bonus option). */
  bonus: number;
  /** In country mode: the country the guess was in (null in the sea, or in pin mode). */
  guessCountry: string | null;
  /** Survival only: the health this guess cost (null otherwise). */
  damage: number | null;
}

export interface PartyRoundReveal {
  roundIndex: number;
  location: RevealedLocation;
  guesses: PartyGuess[];
  /** Survival in teams: what each team lost this round (its best guess's damage). */
  teamDamage: [number, number] | null;
  /** Take turns: who chose this place (they score the others' average); null for a random one. */
  pickerId: string | null;
}

export type PartyPhase =
  | { kind: 'lobby' }
  | { kind: 'loading' }
  /** Take turns: the round's picker chooses the place; the others wait. */
  | { kind: 'picking'; roundIndex: number; pickerId: string; endsAt: number; previewsLeft: number }
  /** `pickerId`: who chose the place (take turns), who doesn't guess this round. */
  | { kind: 'intro'; roundIndex: number; pano: PanoRef; startsAt: number; pickerId: string | null }
  | {
      kind: 'guessing';
      roundIndex: number;
      pano: PanoRef;
      pickerId: string | null;
      startedAt: number;
      endsAt: number | null;
      guessedPlayerIds: string[];
    }
  | { kind: 'reveal'; roundIndex: number }
  | { kind: 'finished' };

export interface PartySnapshot {
  code: string;
  hostId: string;
  settings: PartySettings;
  /** The teams' names, which players can change in the lobby. */
  teamNames: [string, string];
  /** Survival games only: teams' health (in teams) and whether the game is decided. */
  survival: { teamHealth: [number, number] | null; over: boolean } | null;
  /** Take turns only: the order players pick in (player ids, one turn each per lap). */
  picks: { order: string[] } | null;
  maxPlayers: number;
  players: PartyPlayer[];
  phase: PartyPhase;
  /** Results of every completed round of the current game. */
  history: PartyRoundReveal[];
  /**
   * `guess` is locked in; `pin` is the not-yet-locked marker, kept so a reload restores it.
   * `pick` is the place the picker is looking at, during their turn only.
   * Spectators get an empty `playerId`.
   */
  me: { playerId: string; guess: LatLng | null; pin: LatLng | null; pick: PickPreview | null };
  serverTime: number;
}

export type Ack<T = null> = { ok: true; data: T } | { ok: false; error: string };
export type AckCallback<T = null> = (response: Ack<T>) => void;

export interface ClientToServerEvents {
  'party:create': (payload: z.input<typeof createPartySchema>, ack: AckCallback<{ code: string }>) => void;
  'party:join': (payload: z.input<typeof joinPartySchema>, ack: AckCallback<{ code: string }>) => void;
  'party:leave': (ack?: AckCallback) => void;
  'party:profile': (profile: PlayerProfile, ack: AckCallback) => void;
  'party:settings': (settings: Partial<PartySettings>, ack: AckCallback) => void;
  'party:team': (payload: z.input<typeof teamSchema>, ack: AckCallback) => void;
  'party:team-name': (payload: z.input<typeof teamNameSchema>, ack: AckCallback) => void;
  'party:shuffle-teams': (ack: AckCallback) => void;
  'party:start': (payload: z.input<typeof startGameSchema>, ack: AckCallback) => void;
  'party:guess': (payload: z.input<typeof partyGuessSchema>, ack: AckCallback) => void;
  'party:pin': (payload: z.input<typeof partyPinSchema>) => void;
  'party:unguess': (payload: z.input<typeof unguessSchema>, ack: AckCallback) => void;
  'party:end-round': (ack: AckCallback) => void;
  'party:pick-preview': (payload: z.input<typeof pickPreviewSchema>, ack: AckCallback<PickPreview>) => void;
  'party:pick': (payload: z.input<typeof pickSchema>, ack: AckCallback) => void;
  'party:next': (ack: AckCallback) => void;
  'party:rematch': (ack: AckCallback) => void;
  'party:kick': (payload: z.input<typeof kickSchema>, ack: AckCallback) => void;
  'party:react': (payload: z.input<typeof reactSchema>) => void;
  'party:chat': (payload: z.input<typeof chatSchema>, ack: AckCallback) => void;
  'party:watch': (payload: z.input<typeof watchSchema>, ack: AckCallback<{ code: string }>) => void;
  'time:sync': (ack: (serverTime: number) => void) => void;
}

export interface ServerToClientEvents {
  'party:snapshot': (snapshot: PartySnapshot) => void;
  'party:kicked': () => void;
  /** To spectators: the party they watch is over (everyone left). */
  'party:closed': () => void;
  'party:reaction': (reaction: PartyReaction) => void;
  'party:chat': (message: ChatMessage) => void;
  /** The party's recent messages, sent to a player or spectator as they arrive. */
  'party:chat-history': (messages: ChatMessage[]) => void;
}
